import React, { useState, useEffect, FunctionComponent } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';
import _ from 'lodash';
import * as Yup from 'yup';

import './Home.css';
import {
  Section,
  FormGroup,
  TextboxField,
  TradingBlockLink,
  Svg,
  FooterTeaser,
  SiteLogo,
  SiteName,
  ExternalLink,
  Header,
  SubHeader,
  // useTracking,
  Loading,
} from '@tradingblock/components';
import { config as Config } from '../clientConfig';
import { getEnvironment, getConfig } from '../utils';
import { PasswordChangeInitiateUrl } from '@tradingblock/api';

interface FormValues {
  userName: string;
  email: string;
}

export const ForgotPassword: FunctionComponent<RouteComponentProps> = ({ location }) => {
  const env = getEnvironment(location);
  const isVirtual = env === 'virtual';
  const config = getConfig(env);
  const apiRootUrl = config.authurl || undefined;

  const grp = Config.siteGrp;
  const siteParam = grp ? `?grp=${grp}` : '';
  const group = grp === 'mb' ? 'mb' : 'tb';

  const [pageLoadedWithJs, setPageLoadedWithJs] = useState<boolean>(false);
  const [sentEmail, setSentEmail] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // const { initTracking } = useTracking(config.gaUaTrackingCode, config.ga4TrackingCode);

  useEffect(() => {
    // If JavaScript is disabled, this useEffect won't be fired, and the form won't be rendered.
    setPageLoadedWithJs(true);
    // initTracking();
  }, []);

  // handle submit
  const onSubmit = async (values: FormValues) => {
    setIsFetching(true);

    await fetch(`${apiRootUrl}/${PasswordChangeInitiateUrl}?siteid=${group}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userName: values.userName,
        email: values.email,
      }),
    })
      .then(() => {
        setIsFetching(false);
        setSentEmail(true);
      })
      .catch(() => {
        setIsFetching(false);
        setSentEmail(true);
      });
  };

  return (
    <div className={isVirtual ? 'env-virtual' : ''} style={{ fontFamily: 'Poppins, sans-serif' }}>
      <Header>
        <SubHeader>
          <SiteLogo siteGroup={group} />
          <nav className="nav-utilities">
            <ul>
              <li>
                {isVirtual ? (
                  <ExternalLink href={Config.goToLiveUrl + siteParam}>Go to live trading account</ExternalLink>
                ) : (
                  <TradingBlockLink siteGroup={group} origin={group} to="LegacySiteHomeUrl">
                    &uarr; Go to <SiteName siteGroup={group} /> Legacy
                  </TradingBlockLink>
                )}
              </li>
              <li>
                <TradingBlockLink siteGroup={group} origin={group} to="CustomerServiceUrl">
                  Contact us
                </TradingBlockLink>
              </li>
            </ul>
          </nav>
        </SubHeader>
      </Header>
      <main className="login">
        <Section>
          <div className="content">
            <div className="login-container">
              <div className="login-primary">
                <h1 className="mb-3">
                  <i aria-hidden="true" className={`fal ${isVirtual ? 'fa-vr-cardboard' : 'fa-lock-alt'}`} />{' '}
                  {isVirtual ? 'Virtual Forgot Password' : 'Forgot Password'}
                </h1>

                {isFetching && (
                  <div className="d-flex justify-content-center align-items-center">
                    <Loading />
                  </div>
                )}

                {!sentEmail && !isFetching && (
                  <>
                    <p>
                      Please enter the username and email address associated with your{' '}
                      {isVirtual ? 'Virtual Trading' : ''} account.
                    </p>

                    {isVirtual && <p>NOTE: Your live account login is not compatible with Virtual Trading.</p>}

                    {pageLoadedWithJs && (
                      <Formik
                        validationSchema={Yup.object<FormValues>().shape({
                          userName: Yup.string()
                            .trim()
                            .required('Username is required.'),
                          email: Yup.string()
                            .email('Invalid email address.')
                            .trim()
                            .required('Email is required.'),
                        })}
                        validateOnChange
                        initialValues={
                          {
                            userName: '',
                            email: '',
                          } as FormValues
                        }
                        onSubmit={onSubmit}
                      >
                        {() => (
                          <Form translate={undefined}>
                            <FormGroup>
                              <Field component={TextboxField} id="userName" placeholder="Username" autoFocus />
                            </FormGroup>
                            <FormGroup>
                              <Field component={TextboxField} id="email" placeholder="Email" />
                            </FormGroup>
                            <button
                              className={`btn btn-block btn-light btn-icon ${isVirtual ? 'btn-virtual' : ''}`}
                              type="submit"
                            >
                              Submit
                              <span className="tb-icon">
                                {' '}
                                {isVirtual ? <i className="fal fa-vr-cardboard" /> : <Svg path="icon-arrow" />}
                              </span>
                            </button>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </>
                )}

                {sentEmail && (
                  <p>
                    If the username and email provided match what we have on file for your account, you will receive an
                    email with instructions on how to reset your password. Please check your spam or junk folder if this
                    email does not arrive in your Inbox.
                  </p>
                )}

                <noscript>
                  <div className="alert alert-danger">
                    <h4 className="error" style={{ marginBottom: 0 }}>
                      Javascript is required for this site.
                    </h4>
                  </div>
                </noscript>

                <hr />

                <p>
                  Remember your password? <Link to="/">Log in</Link>
                </p>

                <p>
                  New to <SiteName siteGroup={group} />?{' '}
                  {isVirtual ? (
                    <ExternalLink href={Config.virtual.applicationUrl + siteParam}>
                      Open a Virtual Trading account
                    </ExternalLink>
                  ) : (
                    <TradingBlockLink to="OpenNewAccountUrl" siteGroup={group} origin={group} config={config}>
                      Open an account
                    </TradingBlockLink>
                  )}
                </p>

                {!isVirtual && (
                  <p>
                    <i className="fal fa-vr-cardboard" /> Want to test new ideas?{' '}
                    <ExternalLink href={Config.virtual.defaultRedirect + siteParam}>
                      Log in to your Virtual Trading account
                    </ExternalLink>
                  </p>
                )}
              </div>

              <div className="login-secondary">
                <img alt="" aria-hidden="true" src="/images/screen-desktop.png" />
              </div>
            </div>
          </div>
        </Section>
      </main>
      <footer className="footer">
        <div className="content">
          <FooterTeaser siteGroup={group} />
        </div>
      </footer>
    </div>
  );
};
